import { AlertColor, Box, Chip, Grid, Tab, Tabs, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { identifiers } from 'utils/constants/identifiers';
import { fetchFromStorage, saveToStorage } from 'utils/storage';
import strings from '../../../../../common/Translation/Translate';
import Notification from '../../../../../components/Notification';
import axiosInstance from '../../../../../utils/axios';
import { bookingConfirmationBy, dataFlag } from '../../../../../utils/constants/data';
import { routes } from '../../../../../utils/constants/routes';
import { URLS } from '../../../../../utils/constants/urls';
import { createDropdownData } from '../../../../../utils/validators/HelperFunctions';
import { TabPanel } from 'utils/validators/HelperComponents';
import Navigation from 'utils/hooks/Navigation';
import { pageTitleSx } from 'utils/styles/pageHeader';
import { GeneralTab } from './AddEditOrgTabs/GeneralTab';
import { MediaInfoTab } from './AddEditOrgTabs/MediaInfoTab';
import { LocalizedAttribute } from './AddEditOrgTabs/LocalizedAttribute';
import { MailConfigurationTab } from './AddEditOrgTabs/MailConfigurationTab';
import OrgMasterDataList from '../../OrgMasterData/OrgMasterDataList';
import { useSelector } from 'react-redux';
import { Privilege } from '../../../../../utils/redux/reducer/authentication-slice';
import { privileges } from '../../../../../utils/constants/privileges';

const AddEditOrganization: FC = () => {
  const { id } = useParams() as any;
  const userPrivileges = useSelector(Privilege);
  const canEditMailConfiguration = userPrivileges.includes(privileges.edit_email_server_config);
  const canViewOrgMasterData = userPrivileges.includes(privileges.view_org_master_data);
  let nextTabIndex = 3;
  const orgMasterDataTabIndex = canViewOrgMasterData ? nextTabIndex++ : -1;
  const mailConfigurationTabIndex = canEditMailConfiguration ? nextTabIndex++ : -1;

  const [generalPageResponse, setGeneralPageResponse] = useState<any>([]);
  const [location, setLocation] = useState<any>([]);
  const org = fetchFromStorage(identifiers.organization) as any;
  const [initialOrg, setInitialOrg] = useState<any>(null);
  const [value, setValue] = useState(parseInt(fetchFromStorage('orgTabValue') ?? '0'));
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({
    display: false,
    severity: null,
    message: '',
  });


  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    saveToStorage('orgTabValue', newValue.toString());
  };

  useEffect(() => {
    setInitialOrg(org);
    fetchOrganizationLocation();
  }, []);

  const fetchOrganizationLocation = async () => {
    const { data } = await axiosInstance.get(URLS.organizationLocation);
    setLocation(data);
  };

  const dropdownValue = useMemo(() => {
    return {
      roomsAvailable: createDropdownData(dataFlag(strings), ['id', 'name']),
      bookingConfirmationBy: createDropdownData(bookingConfirmationBy(strings), ['id', 'name']),
      location: createDropdownData(location, ['id', 'name']),
      showRFQForExceedingBookingWindowTimeFrame: createDropdownData(dataFlag(strings), ['id', 'name']),
      showAlternativeDates: createDropdownData(dataFlag(strings), ['id', 'name']),
    };
  }, [location]);


  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const handleCopyUrl = (event: any) => {
    event.preventDefault();
    navigator.clipboard.writeText(generalPageResponse.organizationPreviewUrl);
    setMessage({
      display: true,
      severity: 'success',
      message: strings.URL_copied_successfully,
    });
    setTimeout(() => {
      closeNotification(false);
    }, 1500);
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <Grid container>
        <Grid item xs={10}>
          <Typography sx={pageTitleSx}> {id ? strings.organization_text_edit_org : strings.organization_text_add_org} </Typography>
        </Grid>
        {initialOrg !== '*' && <Grid item textAlign="right" xs={2}>
          <Navigation url={routes.organization} />
        </Grid>
        }
      </Grid>
      {generalPageResponse.organizationPreviewUrl ? (
        <Box sx={{}}>
          <Chip color="info" label={strings.organization_text_copy_url} onClick={handleCopyUrl} />
        </Box>
      ) : (
        ''
      )}
      <Box sx={{ p: 0, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={handleChange} aria-label="basic tabs example" data-testId={'tab'}>
          <Tab label={strings.organizations_tabs_general} {...a11yProps(0)} />
          <Tab
            disabled={id === undefined}
            label={strings.organizations_tabs_media_info}
            {...a11yProps(1)}
          />
          <Tab
            disabled={id === undefined}
            label={strings.organizations_tabs_attributes}
            {...a11yProps(2)}
          />
          {canViewOrgMasterData && (
            <Tab
              disabled={id === undefined}
              label={strings.menu_text_organization_master_data}
              {...a11yProps(orgMasterDataTabIndex)}
            />
          )}
          {canEditMailConfiguration && (
            <Tab
              disabled={id === undefined}
              label={strings.organizations_tabs_mail_configuration}
              {...a11yProps(mailConfigurationTabIndex)}
            />
          )}
        </Tabs>
      </Box>
      <TabPanel value={value} index={0}>
        <GeneralTab
          setTabValue={setValue}
          pageResponse={generalPageResponse}
          setPageResponse={setGeneralPageResponse}
          dropdownValue={dropdownValue}
          initialOrg={initialOrg}
        />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <MediaInfoTab setTabValue={setValue}
        />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <LocalizedAttribute />
      </TabPanel>
      {canViewOrgMasterData && (
        <TabPanel value={value} index={orgMasterDataTabIndex}>
          <OrgMasterDataList isParent={false} />
        </TabPanel>
      )}
      {canEditMailConfiguration && (
        <TabPanel value={value} index={mailConfigurationTabIndex}>
          <MailConfigurationTab />
        </TabPanel>
      )}
    </>
  );
};
export default AddEditOrganization;
